import { LitElement, html, css, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { pageContentStyles } from '../styles/page-content.css.js';
import './site-hero-slider.js';
import './site-feature-box.js';
import './site-product-list.js';
import { store } from '../lib/store.js';
import { browserUrl, isDraftPath, prefixLogicalHtml } from '../lib/build-paths.js';

export class SitePageRenderer extends LitElement {
  static properties = {
    content: { type: Object },
    title: { type: String },
  };

  static styles = [
    css`
      :host {
        display: block;
        color: var(--color-text-primary, #f0f0f5);
      }
    `,
    pageContentStyles,
  ];

  render() {
    const content = normalizeContent(this.content, this.title);
    const hasHero = content.elements.some((element) => element.type === 'slideshow');

    return html`
      <article class="page">
        ${hasHero ? '' : html`
          <header class="page__header">
            <h1 class="page__title">${content.meta.headline}</h1>
            ${content.meta.subtitle ? html`<p class="page__subtitle">${content.meta.subtitle}</p>` : ''}
          </header>
        `}
        <div class="elements ${hasHero ? 'elements--flush' : ''}">
          ${renderContentElements(content.elements)}
        </div>
      </article>
    `;
  }

  updated() {
    focusHashTarget(this.renderRoot);
  }
}

function renderText(props) {
  const tag = props.tag || 'p';
  const className = `text text--${props.align || 'left'}`;
  switch (tag) {
    case 'h1':
      return html`<div class=${className}><h1>${props.text || ''}</h1></div>`;
    case 'h2':
      return html`<div class=${className}><h2>${props.text || ''}</h2></div>`;
    case 'h3':
      return html`<div class=${className}><h3>${props.text || ''}</h3></div>`;
    case 'h4':
      return html`<div class=${className}><h4>${props.text || ''}</h4></div>`;
    default:
      return html`<p class=${className}>${props.text || ''}</p>`;
  }
}

export function renderContentElements(elements) {
  return (elements || []).map((element) => renderContentElement(element));
}

export function renderContentElement(element) {
  const draft = isDraftPath();
  return html`<div
    id=${element.id || nothing}
    data-cms-module-id=${element.id || ''}
    tabindex=${draft ? '0' : nothing}
    aria-label=${draft ? `Edit ${element.type} module` : nothing}
    @click=${(event) => selectDraftModule(event, element.id)}
    @keydown=${(event) => selectDraftModule(event, element.id)}
  >${renderContentElementBody(element)}</div>`;
}

export function focusHashTarget(root) {
  let id = '';
  try { id = decodeURIComponent(window.location.hash.replace(/^#/, '')); } catch { return; }
  if (!id || !/^el_[a-zA-Z0-9_-]{6,64}$/.test(id)) return;
  const target = [...root.querySelectorAll('[data-cms-module-id]')].find((node) => node.dataset.cmsModuleId === id);
  if (!target || target.dataset.hashFocused === 'true') return;
  target.dataset.hashFocused = 'true';
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  target.focus({ preventScroll: true });
}

function renderContentElementBody(element) {
  switch (element.type) {
    case 'slideshow':
      return html`<site-hero-slider .slides=${element.props.slides || []}></site-hero-slider>`;
    case 'text':
      return renderText(element.props);
    case 'rich_text':
      return html`<div class="rich">${unsafeHTML(prefixLogicalHtml(element.props.html || ''))}</div>`;
    case 'image':
      return html`
        <figure class="image">
          <img src=${browserUrl(element.props.src || '')} alt=${element.props.alt || ''}>
          ${element.props.caption ? html`<figcaption>${element.props.caption}</figcaption>` : ''}
        </figure>
      `;
    case 'grid':
      return renderGrid(element.props);
    case 'products':
      return html`<site-product-list
        .items=${element.props.items || []}
        .pageSize=${element.props.pageSize || 12}
        .columns=${element.props.columns || 3}
      ></site-product-list>`;
    case 'button':
      return html`<a class="button button--${element.props.variant || 'primary'}" href=${browserUrl(element.props.href || '#')}>${element.props.label || ''}</a>`;
    case 'container':
      return html`<section class="container container--${element.props.variant || 'default'}">${renderContentElements(element.children || [])}</section>`;
    case 'row':
      return html`<div class="row">${renderContentElements(element.children || [])}</div>`;
    case 'columns':
      return html`<div class="columns" style=${`--columns:${element.props.columns || 2}`}>${renderContentElements(element.children || [])}</div>`;
    default:
      return '';
  }
}

export function selectDraftModule(event, id) {
  if (!id || window.parent === window || !location.pathname.includes('/drafts/')) return;
  if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  event.stopPropagation();
  window.parent.postMessage({ type: 'sitecms:module-selected', moduleId: id }, location.origin);
}

export function highlightDraftModule(id) {
  const matches = [];
  const visit = (root) => {
    root.querySelectorAll?.('[data-cms-module-id]').forEach((element) => {
      const selected = Boolean(id) && element.dataset.cmsModuleId === id;
      element.style.outline = selected ? '3px solid #2563eb' : '';
      element.style.outlineOffset = selected ? '3px' : '';
      element.toggleAttribute('data-cms-module-selected', selected);
      if (selected) matches.push(element);
      if (element.shadowRoot) visit(element.shadowRoot);
    });
    root.querySelectorAll?.('*').forEach((element) => {
      if (element.shadowRoot) visit(element.shadowRoot);
    });
  };
  visit(document);
  matches[0]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  return matches.length;
}

window.addEventListener('message', (event) => {
  if (event.origin !== location.origin || event.source !== window.parent || event.data?.type !== 'sitecms:module-highlight') return;
  const moduleId = typeof event.data.moduleId === 'string' ? event.data.moduleId : '';
  const matchCount = highlightDraftModule(moduleId);
  window.parent.postMessage({ type: 'sitecms:module-highlighted', moduleId, matchCount }, location.origin);
});

function renderGrid(props) {
  const columns = Math.max(1, Math.min(Number(props.columns || 3), 4));
  return html`
    <div class="grid" style=${`--columns:${columns}`}>
      ${(props.items || []).map((item) => html`
        <div class="grid__item">
          ${item.image ? html`<img class="grid__image" src=${browserUrl(item.image)} alt=${item.title || ''}>` : ''}
          <site-feature-box
            icon=${item.icon || ''}
            title=${item.title || ''}
            description=${item.description || ''}
          ></site-feature-box>
          ${item.link ? html`<a class="grid__link" href=${browserUrl(item.link)}>${item.linkText || store.getSiteSettings()?.interface?.default_link_text || ''}</a>` : ''}
        </div>
      `)}
    </div>
  `;
}

function normalizeContent(content, title) {
  if (content?.schema_version === 1 && Array.isArray(content.elements)) {
    return content;
  }
  return {
    schema_version: 1,
    template: content?.template || 'built-in',
    meta: {
      headline: content?.headline || title || __SITE_CONFIG__.name || 'Website',
      subtitle: content?.subtitle || '',
      description: content?.description || content?.subtitle || '',
    },
    elements: [],
  };
}

customElements.define('site-page-renderer', SitePageRenderer);
